// `/login`: the password form (ADR 0009). It is where every signed-out visit is sent,
// with the page it came from as `returnTo`, and where better-auth sends a browser when an
// MCP client's authorization needs a sign-in, with that request, signed, as the query
// string.
//
// Signing in happens in the browser, through better-auth's client: its email sign-in (a
// username is the user's email address), which starts the session, with `returnTo` as its
// `callbackURL`. For an MCP client's request the client also hands better-auth the signed
// request, and better-auth carries on with that authorization instead; either way
// better-auth says where the browser goes next, and its client goes there.
// The Field schema (./login.form) gates the submission; better-auth decides whether the
// credentials are right.
import { useState, type FormEvent } from "react";
import { redirect, useLoaderData } from "react-router";
import { useForm, useStore } from "@tanstack/react-form-remix";
import { CerbosLogo } from "~/components/CerbosLogo";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { authClient } from "~/lib/auth/client";
import { getAuthorizingUser, getSessionRow } from "~/lib/auth/index.server";
import { safeReturnTo } from "~/lib/auth/return-to.server";
import { readSignInStep } from "~/lib/auth/sign-in-step.server";
import { errorMessages } from "~/lib/forms";
import { loginFormOptions } from "./login.form";
import type { Route } from "./+types/login";

/**
 * Third-party clients send people here to sign in, so the page must never render inside
 * someone else's frame, where a password could be clickjacked out of it.
 */
export function headers() {
  return { "Content-Security-Policy": "frame-ancestors 'none'", "X-Frame-Options": "DENY" };
}

/**
 * A visitor who is already signed in goes straight to `returnTo`; one signed in as a
 * reader is told why that account cannot open Cerbie. A request that is forged or has
 * expired is not one to sign in to; the page starts over as a plain sign-in.
 *
 * `returnTo` is sanitised here (`safeReturnTo`) because this redirect is the loader's own:
 * better-auth checks a sign-in's `callbackURL`, but has nothing for a visitor who arrives
 * signed in.
 */
export async function loader({ request }: Route.LoaderArgs) {
  const search = new URL(request.url).searchParams;
  const returnTo = safeReturnTo(search.get("returnTo"));
  const step = await readSignInStep(request);

  if (!step) {
    if (search.has("sig")) throw redirect("/login");

    if (await getSessionRow(request)) throw redirect(returnTo);

    // oxlint-disable-next-line cerbie/no-role-checks -- tells a reader account why it cannot open the screens (ADR 0009)
    return { returnTo, signedInAsReader: (await getAuthorizingUser(request))?.role === "reader" };
  }

  return { returnTo, signedInAsReader: false };
}

/** What better-auth's refusal says to the person signing in. */
function refusalMessage(error: { code?: string; message?: string }): string {
  if (error.code === "BANNED_USER") {
    return error.message ?? "Your account has been disabled. Contact your admin.";
  }

  if (error.code === "INVALID_EMAIL_OR_PASSWORD" || error.code === "INVALID_EMAIL") {
    return "Invalid username or password.";
  }

  return "Something went wrong. Please try again.";
}

export default function LoginPage() {
  const { returnTo, signedInAsReader } = useLoaderData<typeof loader>();
  // DESIGN POINT — a wrong username/password is not a field-shape error. Neither field is
  // malformed; the pair is simply not a real account, which only better-auth knows. So it
  // is shown once, above the button, as a form-level alert, never on either field.
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    ...loginFormOptions,
    // The message is about the attempt, so it goes as soon as the SDR edits the form.
    listeners: { onChange: () => setFormError(null) },
    onSubmit: async ({ value }) => {
      setFormError(null);

      // A username is an email address; better-auth lowercases it, and the page trims it.
      // better-auth refuses a `callbackURL` off this origin, and its client follows the
      // one it answers with: `returnTo`, or, part of an MCP client's authorization, the
      // next step of that.
      const { error } = await authClient.signIn.email({
        email: value.username.trim(),
        password: value.password,
        callbackURL: returnTo,
      });

      if (error) {
        // Retyping a password after a failed sign-in is what a browser does anyway; the
        // username the SDR already got right stays. Cleared quietly: nobody typed it, so it
        // is neither an edit nor a field to blame.
        form.setFieldValue("password", "", {
          dontUpdateMeta: true,
          dontValidate: true,
          dontRunListeners: true,
        });
        setFormError(refusalMessage(error));
      }
    },
  });

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const formErrorId = "login-error";

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void form.handleSubmit();
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-8 px-4">
        <div className="flex flex-col items-center gap-4">
          <CerbosLogo className="h-12 w-12" />
          <h1 className="text-center text-2xl font-semibold tracking-tight">Cerbie Assistant</h1>
        </div>

        {signedInAsReader && (
          <p role="status" className="text-sm text-muted-foreground">
            Signed in with a reader account. Sign in with your own account to use Cerbie.
          </p>
        )}

        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <form.Field name="username">
            {(field) => {
              const messages = errorMessages(field.state.meta.errors);
              const invalid = messages.length > 0;

              return (
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    name={field.name}
                    type="email"
                    placeholder="Your email address"
                    autoComplete="username"
                    autoFocus
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                    onBlur={field.handleBlur}
                    aria-invalid={invalid}
                    aria-describedby={invalid ? "username-error" : undefined}
                  />
                  {invalid && (
                    <p id="username-error" role="alert" className="text-sm text-destructive">
                      {messages.join(" ")}
                    </p>
                  )}
                </div>
              );
            }}
          </form.Field>

          <form.Field name="password">
            {(field) => {
              const messages = errorMessages(field.state.meta.errors);
              const invalid = messages.length > 0;

              return (
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    name={field.name}
                    type="password"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    value={field.state.value}
                    onChange={(event) => field.handleChange(event.target.value)}
                    onBlur={field.handleBlur}
                    aria-invalid={invalid}
                    aria-describedby={invalid ? "password-error" : undefined}
                  />
                  {invalid && (
                    <p id="password-error" role="alert" className="text-sm text-destructive">
                      {messages.join(" ")}
                    </p>
                  )}
                </div>
              );
            }}
          </form.Field>

          {formError && (
            <p id={formErrorId} role="alert" className="text-sm text-destructive">
              {formError}
            </p>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting}
            aria-busy={isSubmitting}
            aria-describedby={formError ? formErrorId : undefined}
          >
            {isSubmitting ? "Signing in..." : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export function meta() {
  return [{ title: "Sign in · Cerbie Assistant" }];
}
